import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getCurrentStore, updateCurrentStore, type StoreSettingsUpdate } from '@/lib/api/stores.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import {
  SETTINGS_TIMEZONES,
  WEEKDAY_DEFAULTS,
  buildStoreHoursPayload,
  nonEmptyText,
  nullableText,
  storeField,
} from '@/pages/settings/settings-utils.js';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges.js';

export function StoreInfoSettingsPage() {
  const queryClient = useQueryClient();
  const storeQuery = useQuery({ queryKey: ['store-current'], queryFn: getCurrentStore });
  const store = storeQuery.data;
  const [form, setForm] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(false);
  useUnsavedChanges(dirty);

  const savedHours = useMemo(
    () => ({
      ...WEEKDAY_DEFAULTS,
      ...((store?.storeHours as typeof WEEKDAY_DEFAULTS | undefined) ?? {}),
    }),
    [store?.storeHours],
  );

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const value = (key: Parameters<typeof storeField>[1], fallback = '') =>
    form[key] ?? storeField(store, key, fallback);

  const setField = (key: string, next: string) => {
    setDirty(true);
    setForm((current) => ({ ...current, [key]: next }));
  };

  const mutation = useMutation({
    mutationFn: () => {
      const payload: StoreSettingsUpdate = {
        name: nonEmptyText(value('name'), store?.name ?? 'Store'),
        phone: nullableText(value('phone')),
        email: nullableText(value('email')),
        website: nullableText(value('website')),
        address: nullableText(value('address')),
        city: nullableText(value('city')),
        state: nullableText(value('state')),
        zip: nullableText(value('zip')),
        country: nullableText(value('country', 'United States')) ?? 'United States',
        timezone: nonEmptyText(value('timezone'), store?.timezone ?? 'America/Detroit'),
        currency: 'USD',
        storeHours: buildStoreHoursPayload(savedHours, form),
        vipLabel: nonEmptyText(value('vipLabel'), store?.vipLabel ?? 'VIP'),
        flaggedLabel: nonEmptyText(value('flaggedLabel'), store?.flaggedLabel ?? 'Flagged'),
      };
      return updateCurrentStore(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-current'] });
      setDirty(false);
      setForm({});
      toastSuccess('Store info saved');
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  if (storeQuery.isLoading) {
    return <div className="rounded-2xl bg-white p-6 text-slate-500">Loading store…</div>;
  }

  return (
    <form
      className="space-y-6"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        mutation.mutate();
      }}
    >
      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-bold text-slate-950">Basic Information</h2>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          {(
            [
              ['name', 'Store Name *'],
              ['phone', 'Phone *'],
              ['email', 'Email'],
              ['website', 'Website'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="block">
              <span className="text-sm font-medium text-slate-700">{label}</span>
              <input
                required={key === 'name' || key === 'phone'}
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary"
                value={value(key)}
                onChange={(e) => setField(key, e.target.value)}
              />
            </label>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-bold text-slate-950">Address</h2>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <label className="block md:col-span-2">
            <span className="text-sm font-medium text-slate-700">Street</span>
            <input
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary"
              value={value('address')}
              onChange={(e) => setField('address', e.target.value)}
            />
          </label>
          {(
            [
              ['city', 'City'],
              ['state', 'State'],
              ['zip', 'ZIP'],
              ['country', 'Country'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="block">
              <span className="text-sm font-medium text-slate-700">{label}</span>
              <input
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary"
                value={value(key, key === 'country' ? 'United States' : '')}
                onChange={(e) => setField(key, e.target.value)}
              />
            </label>
          ))}
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Timezone</span>
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary"
              value={value('timezone', 'America/Detroit')}
              onChange={(e) => setField('timezone', e.target.value)}
            >
              {SETTINGS_TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-bold text-slate-950">Customer Status Labels</h2>
        <p className="mt-1 text-sm text-slate-500">Customize VIP and flagged badges shown across POS.</p>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">VIP Label</span>
            <input
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary"
              value={value('vipLabel', store?.vipLabel ?? 'VIP')}
              onChange={(e) => setField('vipLabel', e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Flagged Label</span>
            <input
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary"
              value={value('flaggedLabel', store?.flaggedLabel ?? 'Flagged')}
              onChange={(e) => setField('flaggedLabel', e.target.value)}
            />
          </label>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-bold text-slate-950">Branding</h2>
        <div className="mt-4 rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
          {store?.logoUrl ? (
            <img src={store.logoUrl} alt="Store logo" className="mx-auto mb-3 h-16 object-contain" />
          ) : (
            <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-lg bg-slate-100 text-xs">
              No logo
            </div>
          )}
          <p>Logo upload coming soon.</p>
        </div>
        <label className="mt-4 block">
          <span className="text-sm font-medium text-slate-700">Store Currency</span>
          <input className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-slate-500" value="USD" disabled />
        </label>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-bold text-slate-950">Store Hours</h2>
        <p className="mt-1 text-sm text-slate-500">Used for promised date calculations and customer-facing hours.</p>
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase text-slate-500">
                <th className="pb-2 pr-4">Day</th>
                <th className="pb-2 pr-4">Open</th>
                <th className="pb-2 pr-4">Close</th>
                <th className="pb-2">Closed</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(savedHours).map(([day, hours]) => (
                <tr key={day} className="border-t border-slate-100">
                  <td className="py-2 pr-4 capitalize font-medium">{day}</td>
                  <td className="py-2 pr-4">
                    <input
                      type="time"
                      className="rounded-lg border border-slate-300 px-2 py-1"
                      value={form[`hours.${day}.open`] ?? hours.open}
                      onChange={(e) => setField(`hours.${day}.open`, e.target.value)}
                    />
                  </td>
                  <td className="py-2 pr-4">
                    <input
                      type="time"
                      className="rounded-lg border border-slate-300 px-2 py-1"
                      value={form[`hours.${day}.close`] ?? hours.close}
                      onChange={(e) => setField(`hours.${day}.close`, e.target.value)}
                    />
                  </td>
                  <td className="py-2">
                    <input
                      type="checkbox"
                      checked={(form[`hours.${day}.closed`] ?? String(hours.closed)) === 'true'}
                      onChange={(e) => setField(`hours.${day}.closed`, String(e.target.checked))}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <button
        type="submit"
        disabled={!dirty || mutation.isPending}
        className="rounded-xl bg-primary px-6 py-2.5 font-semibold text-white disabled:opacity-50"
      >
        {mutation.isPending ? 'Saving…' : 'Save Store Info'}
      </button>
    </form>
  );
}
